// services/getNearMeLocations.ts
import {
  GeoPoint,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  Query,
} from "firebase/firestore";
import { app, db } from "../../firebaseConfig";
import * as turf from "@turf/turf";
import { Offer } from "../../lib/types/detailsTypes";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Location } from "../location/locationCard";

// Function to fetch nearby locations for an offer
export async function getNearbyOfferLocations(
  center: GeoPoint,
  storeId: string,
  acceptedIds: string[],
  radiusInKm = 2
): Promise<Location[]> {
  if (!acceptedIds.length) return [];

  // Create a Turf point for the center using GeoPoint properties
  const centerPoint = turf.point([center.longitude, center.latitude]);
  // Buffer the point by the desired radius to create a circular area
  const buffered = turf.buffer(centerPoint, radiusInKm, {
    units: "kilometers",
  });

  // Ensure the buffered result is valid
  if (!buffered) {
    throw new Error("Buffering operation failed.");
  }

  // Derive the bounding box from the buffered circle
  const [minLon, minLat, maxLon, maxLat] = turf.bbox(buffered);
  const locationsRef = collection(db, "locations");
  const q = query(
    locationsRef,
    where("storeId", "==", storeId),
    where("lat", ">=", minLat),
    where("lat", "<=", maxLat),
    where("lng", ">=", minLon),
    where("lng", "<=", maxLon)
  );

  const snap = await getDocs(q);

  // --- 3  distance, lng filter, acceptedIds filter ---
  const results: Location[] = [];
  snap.forEach((doc) => {
    const data = doc.data() as Location;

    // fast check on lng to cut false positives early
    if (data.lng < minLon || data.lng > maxLon) return;

    // must be one of the accepted location IDs
    if (!acceptedIds.includes(data.locationId)) return;

    // Calculate the actual distance using Turf.js
    const locPoint = turf.point([data.lng, data.lat]);
    const distance = turf.distance(centerPoint, locPoint, {
      units: "kilometers",
    });

    if (distance <= radiusInKm) {
      results.push({ ...data, distance });
    }
  });
  return results.sort((a, b) => a.distance - b.distance);
}

// Returns an array of location documents with a calculated distance property
export async function getNearMeLocations(
  center: GeoPoint,
  radiusInKm: number
): Promise<any[]> {
  // Create a Turf point for the center using GeoPoint properties
  const centerPoint = turf.point([center.longitude, center.latitude]);
  // Buffer the point by the desired radius to create a circular area
  const buffered = turf.buffer(centerPoint, radiusInKm, {
    units: "kilometers",
  });

  // Ensure the buffered result is valid
  if (!buffered) {
    throw new Error("Buffering operation failed.");
  }

  // Derive the bounding box from the buffered circle
  const [minLon, minLat, maxLon, maxLat] = turf.bbox(buffered);

  // Query Firestore using inequality filters on the separate lat and lng fields
  const locationsRef = collection(db, "locations");
  const q = query(
    locationsRef,
    where("lat", ">=", minLat),
    where("lat", "<=", maxLat),
    where("lng", ">=", minLon),
    where("lng", "<=", maxLon)
  );

  const snapshot = await getDocs(q);
  const results: any[] = [];

  snapshot.forEach((doc) => {
    const data = doc.data();
    // Calculate the actual distance using Turf.js with numeric lat and lng fields
    const locPoint = turf.point([data.lng, data.lat]);
    const distance = turf.distance(centerPoint, locPoint, {
      units: "kilometers",
    });
    results.push({ ...data, distance });
  });

  // Filter out any false positives and sort the results by distance
  return results
    .filter((item) => item.distance <= radiusInKm)
    .sort((a, b) => a.distance - b.distance);
}

// fetch location info for selected location with distance calculation
export const fetchLocationInfo = async (
  locationId: string,
  center: GeoPoint
): Promise<any | null> => {
  const locationRef = doc(db, "locations", locationId);
  const snapshot = await getDoc(locationRef);
  const data = snapshot.data();
  if (!data) {
    return null;
  }

  // Create Turf points for the center and the location
  const centerPoint = turf.point([center.longitude, center.latitude]);
  const locationPoint = turf.point([data.lng, data.lat]);

  // Calculate the distance in kilometers
  const distance = turf.distance(centerPoint, locationPoint, {
    units: "kilometers",
  });

  // Attach the calculated distance to the location data
  return { ...data, distance };
};

// fetch offers for selected location or store

export const fetchOffers = async ({
  storeId,
  locationId,
}: {
  storeId?: string;
  locationId?: string;
}): Promise<Offer[]> => {
  const offersRef = collection(db, "offers");
  let q: Query;

  if (storeId) {
    q = query(
      offersRef,
      where("storeId", "==", storeId),
      where("isActive", "==", true)
    );
  } else if (locationId) {
    q = query(
      offersRef,
      where("acceptedLocations", "array-contains", locationId),
      where("isActive", "==", true)
    );
  } else {
    throw new Error("Either storeId or locationId must be provided");
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const { id: _, ...data } = doc.data() as Offer;
    return { id: doc.id, ...data };
  });
};

// Copy offer to wallet

export type CopyOfferResult = {
  success: boolean;
  message: string;
};

export const copyOfferToWallet = async (
  offerId: string
): Promise<{ success: boolean; message: string }> => {
  const functions = getFunctions(app);
  // Call the copyOfferCF function from Firebase Functions
  const copyOfferCF = httpsCallable(functions, "copyOfferCF");
  try {
    // Call the function with the offerId parameter.
    const result = await copyOfferCF({ offerId });

    const { success, message } = result.data as CopyOfferResult;

    // Return the result so it can be used elsewhere in your application.
    return { success, message };
  } catch (error) {
    // Log and return the error message.
    console.error("Error calling Cloud Function:", error);
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred.";
    return { success: false, message: errorMessage };
  }
};

// Fetch all offers for a specific customer
export const fetchWalletOffers = async (userId: string): Promise<Offer[]> => {
  try {
    const offersRef = collection(db, "users", userId, "myOffers");
    // Create a query that filters offers where isExpired is false
    const q = query(offersRef, where("isExpired", "==", false));
    const snapshot = await getDocs(q);

    // Map over each document in the snapshot and convert it to an Offer object.
    const offers: Offer[] = snapshot.docs.map((doc) => {
      return { id: doc.id, ...doc.data() } as Offer;
    });

    return offers;
  } catch (error) {
    console.error("Error fetching wallet offers:", error);
    return [];
  }
};

// activate offer in wallet
export async function activateOffer(offerId: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    // Get a reference to Firebase Functions instance
    const functions = getFunctions();
    // Create a callable function for activateOfferCF
    const activateOfferCF = httpsCallable(functions, "activateOfferCF");

    // Invoke the cloud function with the offerId
    const result = await activateOfferCF({ offerId });

    // The callable function response is inside result.data
    // Casting result.data to the appropriate structure you expect
    const { success, message } = result.data as {
      success: boolean;
      message: string;
    };

    return { success, message };
  } catch (error: any) {
    // Catch any callable function errors
    return {
      success: false,
      message: error?.message || "Unknown error",
    };
  }
}

// delete offer in wallet
export async function deleteOffer(offerId: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    // get reference to document in wallet
    const functions = getFunctions(app);
    const deleteOfferCF = httpsCallable(functions, "deleteOfferCF");
    // Call the Cloud Function to delete the offer
    const result = await deleteOfferCF({ offerId });
    // The callable function response is inside result.data
    const { success, message } = result.data as {
      success: boolean;
      message: string;
    };

    return { success, message };
  } catch (error: any) {
    // Catch any callable function errors
    return {
      success: false,
      message: error?.message || "Unknown error",
    };
  }
}

// expire offer in wallet
export async function expireOffer(offerId: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    // get reference to document in wallet
    const functions = getFunctions(app);
    const expireOfferCF = httpsCallable(functions, "expireOfferInWalletCF");
    // Call the Cloud Function to delete the offer
    const result = await expireOfferCF({ offerId });
    // The callable function response is inside result.data
    const { success, message } = result.data as {
      success: boolean;
      message: string;
    };

    return { success, message };
  } catch (error: any) {
    // Catch any callable function errors
    return {
      success: false,
      message: error?.message || "Unknown error",
    };
  }
}
